/**
 * Sprint 2 — Zaroori Jini OMS: Marketplace + Industry OMS Pages
 *
 * 14 "OMS for {Marketplace}" + 14 "{Industry} OMS" = 28 auto-built pages.
 * URL patterns:
 *   /zaroori-jini-oms/oms-for-<marketplace-slug>
 *   /zaroori-jini-oms/<industry-slug>-oms
 */

// ============================================================================
// MARKETPLACE OMS TARGETS
// ============================================================================
export const OMS_MARKETPLACES = [
  { slug: 'myntra', name: 'Myntra', kind: 'marketplace', focus: 'Fashion + footwear + beauty' },
  { slug: 'nykaa', name: 'Nykaa', kind: 'marketplace', focus: 'Beauty + personal care + Nykaa Fashion' },
  { slug: 'ajio', name: 'AJIO', kind: 'marketplace', focus: 'Fashion (mass + AJIO Luxe)' },
  { slug: 'tata-cliq', name: 'Tata CLiQ', kind: 'marketplace', focus: 'Fashion + electronics + Luxury' },
  { slug: 'amazon', name: 'Amazon India', kind: 'marketplace', focus: 'Fashion + electronics + grocery + luxury' },
  { slug: 'blinkit', name: 'Blinkit', kind: 'quick-commerce', focus: '10-min grocery + FMCG + personal care' },
  { slug: 'zepto', name: 'Zepto', kind: 'quick-commerce', focus: '10-min grocery + FMCG' },
  { slug: 'swiggy-instamart', name: 'Swiggy Instamart', kind: 'quick-commerce', focus: '10-15 min grocery + FMCG' },
  { slug: 'shopify', name: 'Shopify', kind: 'storefront', focus: 'D2C storefront + checkout + apps ecosystem' },
  { slug: 'woocommerce', name: 'WooCommerce', kind: 'storefront', focus: 'WordPress-based D2C storefront' },
  { slug: 'magento', name: 'Magento', kind: 'storefront', focus: 'Enterprise Adobe Commerce storefront' },
  { slug: 'opencart', name: 'OpenCart', kind: 'storefront', focus: 'PHP-based value D2C storefront' },
  { slug: 'wix', name: 'Wix', kind: 'storefront', focus: 'Drag-drop D2C storefront + Wix Stores' },
  { slug: 'ondc', name: 'ONDC', kind: 'network', focus: 'Government-led open network for digital commerce' },
];

// ============================================================================
// INDUSTRY OMS TARGETS
// ============================================================================
export const OMS_INDUSTRIES = [
  { slug: 'fashion', name: 'Fashion', returnBand: '25-40%', velocity: 'High' },
  { slug: 'ethnic-wear', name: 'Ethnic Wear', returnBand: '20-30%', velocity: 'Seasonal' },
  { slug: 'footwear', name: 'Footwear', returnBand: '30-45%', velocity: 'High' },
  { slug: 'cosmetics', name: 'Cosmetics', returnBand: '10-15%', velocity: 'High' },
  { slug: 'jewellery', name: 'Jewellery', returnBand: '5-12%', velocity: 'Medium (high AOV)' },
  { slug: 'healthcare', name: 'Healthcare', returnBand: '3-8%', velocity: 'Recurring' },
  { slug: 'food', name: 'Food (F&B)', returnBand: '2-6%', velocity: 'Very High' },
  { slug: 'electronics', name: 'Electronics', returnBand: '8-14%', velocity: 'Medium' },
  { slug: 'home-decor', name: 'Home Decor', returnBand: '10-18%', velocity: 'Low-Medium' },
  { slug: 'fmcg', name: 'FMCG', returnBand: '2-5%', velocity: 'Very High' },
  { slug: 'luxury-fashion', name: 'Luxury Fashion', returnBand: '18-28%', velocity: 'Low (high AOV)' },
  { slug: 'quick-commerce', name: 'Quick Commerce', returnBand: '2-6%', velocity: 'Extreme (10-min)' },
  { slug: 'b2b', name: 'B2B', returnBand: '4-10%', velocity: 'Bulk' },
  { slug: 'b2c', name: 'B2C', returnBand: '10-25%', velocity: 'Variable' },
];

// ============================================================================
// PAGE FACTORIES — generate 28 records
// ============================================================================

const buildMarketplacePage = (m) => ({
  slug: `oms-for-${m.slug}`,
  kind: 'marketplace-oms',
  target: m,
  h1: `OMS for ${m.name} — Zaroori Jini OMS Native Integration`,
  metaTitle: `OMS for ${m.name} — Best Order Management System for ${m.name} Sellers (2026) | Zaroori Retail`,
  metaDescription: `Zaroori Jini OMS is the best OMS for ${m.name} sellers — native 15-min sync, real-time order webhook, AI repricing, and weekly reconciliation. ${m.focus}.`,
  aiSummary: {
    answer: `Zaroori Jini OMS is India's founder-led marketplace-native OMS with a real-time ${m.name} connector — 15-minute inventory sync, idempotent order webhook, category-buyer-aligned PLA bid manager, and weekly auto-reconciliation. Built for ${m.kind === 'quick-commerce' ? 'dark-store network operations' : m.kind === 'storefront' ? 'D2C-to-marketplace multi-channel sync' : m.kind === 'network' ? 'open network digital commerce compliance' : 'D2C brands scaling on ' + m.name}. Live in 3-5 days.`,
    bullets: [
      `Real-time ${m.name} connector: 15-min inventory sync + idempotent order webhook`,
      m.kind === 'quick-commerce' ? 'City-level dark-store inventory allocation + 48-72 hour replenishment forecasting' : m.kind === 'storefront' ? `Bi-directional sync between ${m.name} + Zaroori Jini OMS + 30+ marketplaces` : `Category-buyer-aligned catalog + PLA bidder on ${m.name}`,
      `AI Repricing tuned for ${m.name} category commissions + promo ladder`,
      'Weekly auto-reconciliation with fee audit + dispute filing',
      '3-5 day implementation with founder-led onboarding',
    ],
    keyFacts: [
      { label: 'Sync Frequency', value: '15 minutes' },
      { label: 'Implementation', value: '3-5 days' },
      { label: 'Payment Cycle', value: m.kind === 'quick-commerce' ? '7-14 days' : m.kind === 'storefront' ? 'Instant (payment gateway)' : '15-30 days' },
      { label: 'Best For', value: m.focus },
      { label: 'Starting Price', value: '₹4,999/month' },
      { label: 'Updated', value: 'February 2026' },
    ],
  },
});

const buildIndustryPage = (ind) => ({
  slug: `${ind.slug}-oms`,
  kind: 'industry-oms',
  target: ind,
  h1: `${ind.name} OMS — Zaroori Jini OMS for ${ind.name} Brands`,
  metaTitle: `${ind.name} OMS — Best Order Management System for ${ind.name} Brands (2026) | Zaroori Retail`,
  metaDescription: `Purpose-built OMS for ${ind.name} brands. Handle ${ind.returnBand} return rates, ${ind.velocity.toLowerCase()} velocity, and multi-marketplace sync. Zaroori Jini OMS ships live in 3-5 days.`,
  aiSummary: {
    answer: `Zaroori Jini OMS for ${ind.name} brands is purpose-tuned for ${ind.name.toLowerCase()}-specific challenges: ${ind.returnBand} return rates, ${ind.velocity.toLowerCase()} order velocity, and category-specific compliance (${ind.slug === 'food' || ind.slug === 'fmcg' ? 'FSSAI + MRP labelling + batch codes' : ind.slug === 'cosmetics' ? 'CDSCO + ingredient disclosure' : ind.slug === 'electronics' ? 'BIS + warranty tracking' : 'GST + trademark + brand approval'}). Native connectors to Myntra, Nykaa, AJIO, Amazon, Blinkit, Zepto, Instamart. AI repricing + PLA bid management + weekly auto-reconciliation.`,
    bullets: [
      `Tuned for ${ind.returnBand} category return rate — root-cause analytics + fit/description checks`,
      `Handles ${ind.velocity.toLowerCase()} order velocity across 30+ marketplaces`,
      `Category-specific compliance workflows built-in`,
      `AI Repricing + PLA Bid Manager tuned to ${ind.name} commission bands`,
      '3-5 day implementation with founder-led onboarding',
    ],
    keyFacts: [
      { label: 'Category Return Rate', value: ind.returnBand },
      { label: 'Velocity', value: ind.velocity },
      { label: 'Implementation', value: '3-5 days' },
      { label: 'Marketplaces Supported', value: '30+' },
      { label: 'Starting Price', value: '₹4,999/month' },
      { label: 'Updated', value: 'February 2026' },
    ],
  },
});

export const SPRINT2_PAGES = [
  ...OMS_MARKETPLACES.map(buildMarketplacePage),
  ...OMS_INDUSTRIES.map(buildIndustryPage),
];

export const SPRINT2_BY_SLUG = SPRINT2_PAGES.reduce((acc, p) => { acc[p.slug] = p; return acc; }, {});
export const SPRINT2_PATHS = SPRINT2_PAGES.map((p) => `/zaroori-jini-oms/${p.slug}`);
